import * as d3 from 'd3';
import SimpleVenn from 'simple-venn';

import { app } from '../app';
import colors from './colors';
import {
  randomZeroToN,
  colorByGroup,
  colorByDomain,
  colorByFilter,
  gaussianRandom,
  sortPointsByFilter,
  isPointActive,
} from './helpers';

// This is the order the slides
// are displayed in
export default [
  random1d,
  random2d,
  whiteBox,
  box,
  venn,
  // swarm,
  bubblePack,
  // phyllotaxis,
  blackout,
];

// Pre and post layout were being called
// in a forEach before setting the layout
// meaning we looped over 70k+ points
// three times per layout. Adding these
// helper functions is clunky, but cuts
// the number of iterations
function preLayout(point) {
  point.sx = point.tx;
  point.sy = point.ty;
  point.colorStart = point.colorEnd;
}

function postLayout(point) {
  point.tx = point.x;
  point.ty = point.y;
  point.colorEnd = point.color;
}

/********************
 ********************
 * BLACKOUT
 ********************
 ********************/
function blackout() {
  app.points.forEach(point => {
    preLayout(point);
    point.x = window.innerWidth / 2;
    point.y = window.innerHeight / 2;
    point.color = colors.dark.vector;
    postLayout(point);
  });
}

/********************
 ********************
 * RAND 1D
 ********************
 ********************/
function random1d() {
  app.points.forEach(point => {
    preLayout(point);
    point.color = colors.light.vector;
    point.x = randomZeroToN(window.innerWidth);
    point.y = window.innerHeight / 2;
    postLayout(point);
  });
}

/********************
 ********************
 * RAND 2D
 ********************
 ********************/
function random2d() {
  app.points.forEach(point => {
    preLayout(point);
    point.color = colors.light.vector;
    point.x = randomZeroToN(window.innerWidth);
    point.y = randomZeroToN(window.innerHeight);
    postLayout(point);
  });
}

/********************
 ********************
 * Bubble Pack
 ********************
 ********************/
function bubblePack() {
  const data = { name: 'root', children: [] };
  for (let key in app.groups) {
    data.children.push({
      name: key,
      value: app.groups[key],
    });
  }
  const layout = d3.pack()
    .size([window.innerWidth + 100, window.innerHeight + 100])
    .padding(4);
  const root = d3.hierarchy(data)
    .sum(d => d.value);
  const nodes = root.descendants().slice(1);

  layout(root);

  app.points.forEach(point => {
    preLayout(point);
    const node = nodes[point.group];
    const angle = Math.random() * 2 * Math.PI;
    const radiusSquared = Math.random() * node.r * node.r;
    const x = Math.sqrt(radiusSquared) * Math.cos(angle);
    const y = Math.sqrt(radiusSquared) * Math.sin(angle);
    point.x = x + node.x - 50;
    point.y = y + node.y - 50;
    point.color = colorByGroup(point);
    postLayout(point);
  });
}

/********************
 ********************
 * Venn
 ********************
 ********************/
function venn() {
  const filter1 = app.filter[0];
  const filter2 = app.filter[1];
  const centerX = window.innerWidth / 2;
  const centerY = window.innerHeight / 2;
  const scale = 10;
  let g1 = 0;
  let g2 = 0;
  let b = 0;

  app.points.forEach(point => {
    const filt1 = isPointActive(point, filter1);
    const filt2 = isPointActive(point, filter2);
    if (filt1 && filt2) {
      g1++; g2++; b++;
    } else if (filt1) {
      g1++;
    } else if (filt2) {
      g2++;
    }
  });

  const venn = new SimpleVenn(g1, g2, b, scale);
  const distance = venn.setDistance;
  const r1 = venn.aSetRadius;
  const r2 = venn.bSetRadius;
  const d1 = venn.aSetIntersectDist;
  const aSetX = centerX - d1;
  const bSetX = centerX + distance - d1;

  function randomPointInCircle(x, y, r) {
    const angle = Math.random() * 2 * Math.PI;
    const radiusSquared = Math.random() * r * r;
    const randX = Math.sqrt(radiusSquared) * Math.cos(angle);
    const randY = Math.sqrt(radiusSquared) * Math.sin(angle);
    const result = [Math.round(randX + x), Math.round(randY + y)];
    return result;
  }

  function isPointInCircle(point, x, y, r) {
    const px = point[0];
    const py = point[1];

    return Math.sqrt((px-x) * (px-x) + (py-y) * (py-y)) >= r;
  }

  app.points.forEach(point => {
    preLayout(point);
    const filt1 = isPointActive(point, filter1);
    const filt2 = isPointActive(point, filter2);

    let randomPoint = [0, 0];
    if (filt1 && filt2) {
      // Assign a random position in intersection
      do {
        randomPoint = randomPointInCircle(aSetX, centerY, r1);
      } while (isPointInCircle(randomPoint, bSetX, centerY, r2));

      point.color = colors.green.variedVector();
    } else if (filt1) {
      // Assign a random position in group 1
      do {
        randomPoint = randomPointInCircle(aSetX, centerY, r1);
      } while (!isPointInCircle(randomPoint, bSetX, centerY, r2));

      point.color = colors.blue.variedVector();
    } else if (filt2) {
      // Assign a random position in group 2
      do {
        randomPoint = randomPointInCircle(bSetX, centerY, r2);
      } while (!isPointInCircle(randomPoint, aSetX, centerY, r1));

      point.color = colors.yellow.variedVector();
    }

    if (!(filt1 || filt2)) {
      point.x = centerX;
      point.y = centerY;
      point.color = colors.dark.vector;
    } else {
      point.x = randomPoint[0];
      point.y = randomPoint[1];
    }

    postLayout(point);
  });
}

/********************
 ********************
 * SWARM
 ********************
 ********************/
function swarm() {
  const col2start = Math.floor(window.innerWidth / 3);
  const col3start = col2start * 2;
  const col1 = [0, col2start];
  const col2 = [col2start, col3start];
  const col3 = [col3start, window.innerWidth];

  const ranges = {
    tor: d3.scaleLinear().domain([0, 1]).range(col2),

    zeronet: d3.scaleLinear().domain([0, 1]).range(col1),

    i2p: d3.scaleLinear().domain([0, 1]).range(col3),
  };

  app.points.forEach(point => {
    preLayout(point);
    point.x = ranges[point.domain](gaussianRandom());
    point.y = randomZeroToN(window.innerHeight);
    point.color = colorByDomain(point);
    postLayout(point);
  });
}

/********************
 ********************
 * WHITE BOX
 ********************
 ********************/
function whiteBox() {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const points = app.points;
  const pointWidth = app.pointWidth;

  const numOfPointsY = Math.floor(height / pointWidth);
  const numOfPointsX = Math.floor(points.length / numOfPointsY);

  const leftMargin = Math.floor((width - (numOfPointsX * pointWidth)) / 2);
  const xOffset = Math.floor((pointWidth / 2) + leftMargin);
  const yOffset = Math.floor(pointWidth / 2);

  points.forEach((point, i) => {
    let col = i % numOfPointsX;
    let row = Math.floor(i / numOfPointsX);

    preLayout(point);
    point.x = col * pointWidth + xOffset;
    point.y = row * pointWidth + yOffset;
    point.color = colors.light.vector;
    postLayout(point);
  });
}

/********************
 ********************
 * BOX
 ********************
 ********************/
function box() {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const points = app.points;
  const pointWidth = app.pointWidth;

  const numOfPointsY = Math.floor(height / pointWidth);
  const numOfPointsX = Math.floor(points.length / numOfPointsY);

  const leftMargin = Math.floor((width - (numOfPointsX * pointWidth)) / 2);
  const xOffset = Math.floor((pointWidth / 2) + leftMargin);
  const yOffset = Math.floor(pointWidth / 2);

  let sortData = sortPointsByFilter(points, app.filter[0]);

  sortData.forEach((data, i) => {
    let point = points[data.index];
    let col = i % numOfPointsX;
    let row = Math.floor(i / numOfPointsX);

    preLayout(point);
    point.x = col * pointWidth + xOffset;
    point.y = row * pointWidth + yOffset;
    point.color = colorByFilter(point, app.filter[0]);
    postLayout(point);
  });
}

/********************
 ********************
 * PHYLLOTAXIS
 ********************
 ********************/
function phyllotaxis() {
  const xOffset = window.innerWidth / 2;
  const yOffset = window.innerHeight / 2;

  const domainOrder = ['zeronet', 'tor', 'i2p'];
  const sortData = app.points
    .map((elem, index) => ({ index, domain: elem.domain }))
    .sort((a, b) => domainOrder.indexOf(b.domain) - domainOrder.indexOf(a.domain));

  const theta = Math.PI * (3 - Math.sqrt(5));
  const pointRadius = app.pointWidth / 2;

  sortData.forEach((d, i) => {
    const point = app.points[d.index];
    const index = i % app.points.length;
    const phylloX = pointRadius * Math.sqrt(index) * Math.cos(index * theta);
    const phylloY = pointRadius * Math.sqrt(index) * Math.sin(index * theta);
    preLayout(point);
    point.x = xOffset + phylloX - pointRadius;
    point.y = yOffset + phylloY - pointRadius;
    point.color = colorByDomain(point);
    postLayout(point);
  });
}



// WEBPACK FOOTER //
// ./src/helpers/layouts.js